import React from 'react';
import {
  Table,
  SearchPanel,
  Button,
  Input,
  Spinner,
  Badge,
  Tooltip,
} from '../../../components';
import { usePresentationsManagement } from './hooks/usePresentationsManagement';
import { PresentationModals, PresentationActions } from './components';
import { PRESENTATION_LABELS, PRESENTATION_STATUS_CONFIG } from './constants';
import type { BriefPresentatiionDto } from '../../../services/presentations-management/models/BriefPresentatiionDto';

export const PresentationsManagementTab: React.FC = () => {
  const {
    presentations,
    loading,
    filters,
    pagination,
    selectedPresentation,
    presentationDetails,
    isOpened,
    onOpen,
    onClose,
    isOpenedEditModal,
    onCloseEditModal,
    isOpenedDeleteModal,
    onCloseDeleteModal,
    isOpenedViewModal,
    onCloseViewModal,
    setFilters,
    setSort,
    setPage,
    handleCreatePresentationSubmit,
    handleEditPresentationSubmit,
    handleEditClick,
    handleDeleteClick,
    handleViewClick,
    handleConfirmDelete,
    creatingPresentation,
    updatingPresentation,
    deletingPresentation,
    loadingDetails,
  } = usePresentationsManagement();

  const handleSearchChange = (value: string) => {
    setFilters({ search: value });
  };

  return (
    <div className="p-4">
      <div className="flex flex-wrap justify-between items-end mb-4 gap-y-md">
        <div className="flex gap-x-sm justify-between">
          <div className="w-80">
            <Input
              placeholder={PRESENTATION_LABELS.SEARCH_PRESENTATIONS}
              value={filters?.search || ''}
              onChange={e => handleSearchChange(e)}
            />
          </div>
        </div>

        <div className="flex gap-x-sm">
          <Button
            label={PRESENTATION_LABELS.CREATE_PRESENTATION}
            onClick={onOpen}
            size="medium"
            disabled={creatingPresentation}
          />
        </div>
      </div>

      <PresentationModals
        isOpened={isOpened}
        onClose={onClose}
        isOpenedEditModal={isOpenedEditModal}
        onCloseEditModal={onCloseEditModal}
        isOpenedDeleteModal={isOpenedDeleteModal}
        onCloseDeleteModal={onCloseDeleteModal}
        isOpenedViewModal={isOpenedViewModal}
        onCloseViewModal={onCloseViewModal}
        selectedPresentation={selectedPresentation}
        presentationDetails={presentationDetails}
        handleCreatePresentationSubmit={handleCreatePresentationSubmit}
        handleEditPresentationSubmit={handleEditPresentationSubmit}
        handleConfirmDelete={handleConfirmDelete}
        creatingPresentation={creatingPresentation}
        updatingPresentation={updatingPresentation}
        deletingPresentation={deletingPresentation}
        loadingDetails={loadingDetails}
      />

      <div className="overflow-x-auto">
        <Table
          data={presentations}
          loading={loading}
          emptyMessage={PRESENTATION_LABELS.NO_PRESENTATIONS}
          config={{
            columns: [
              {
                sortName: 'title',
                header: PRESENTATION_LABELS.PRESENTATION_TITLE,
                cellClassName: 'min-w-[15rem] text-left',
                getValue: ({ title, status }) => (
                  <div className="flex items-center gap-4 truncate font-medium">
                    {status === 'IN_PROGRESS' && (
                      <Spinner className="h-10 w-10" />
                    )}
                    {title || 'Untitled'}
                  </div>
                ),
              },
              {
                header: PRESENTATION_LABELS.STATUS,
                cellClassName: 'min-w-[15rem] text-left',
                getValue: ({ status }) => (
                  <div className="truncate font-medium">
                    <Badge
                      config={PRESENTATION_STATUS_CONFIG}
                      status={status || 'UNKNOWN'}
                    />
                  </div>
                ),
              },
              {
                header: PRESENTATION_LABELS.DESCRIPTION,
                cellClassName: 'min-w-[20rem] max-w-[25rem] text-left',
                getValue: ({ description }) => {
                  const displayText = description || '-';
                  const shouldShowTooltip =
                    description && description.length > 50;

                  if (shouldShowTooltip) {
                    return (
                      <div className="relative w-full">
                        <Tooltip
                          content={description}
                          position="top"
                          className="absolute"
                          iconClassName="opacity-0"
                          trigger="hover"
                        >
                          <div className="truncate text-gray-600">
                            {displayText}
                          </div>
                        </Tooltip>
                      </div>
                    );
                  }

                  return (
                    <div className="truncate text-gray-600">{displayText}</div>
                  );
                },
              },
              {
                header: PRESENTATION_LABELS.SLIDE_COUNT,
                cellClassName: 'min-w-[8rem] text-center',
                getValue: ({ slideCount }) => (
                  <span className="inline-flex items-center justify-center whitespace-nowrap px-4 py-2 rounded-full text-tiny font-medium bg-blue-100 text-blue-800">
                    {slideCount || 0} slides
                  </span>
                ),
              },
              {
                header: PRESENTATION_LABELS.ACTIONS,
                cellClassName: 'min-w-[12rem]',
                align: 'center',
                getValue: (presentation: BriefPresentatiionDto) => (
                  <PresentationActions
                    onEdit={() => handleEditClick(presentation)}
                    onDelete={() => handleDeleteClick(presentation)}
                    onView={() => handleViewClick(presentation)}
                  />
                ),
              },
            ],
          }}
        />
      </div>

      <div className="mt-sm flex justify-between items-center">
        <SearchPanel
          totalItems={pagination?.totalItems}
          page={pagination?.page || 1}
          perPage={pagination?.perPage || 10}
          withPaginate
          setPage={setPage}
        />
      </div>
    </div>
  );
};
